<template>
  <div>
    <transition mode="out-in">
      <loading-spinner v-if="loading" />
      <div v-else>
        <div class="mb-2">
          <strong>Transactions</strong>
        </div>
        <div v-for="(transaction, idx) in transactions" :key="idx" class="row border-bottom small align-items-center">
          <div class="col-4 small">{{ (new Date(transaction.transaction_date)).toLocaleDateString() }}</div>
          <div class="col-4 small fw-bold">{{ transactionType(transaction) }}</div>
          <div class="col-3 text-end"><money-block :dollar="transaction.transaction_amount" /></div>
          <div class="col-1 text-end">
            <button
              v-if="isRefund(transaction) && props.isSystem !== 0"
              type="button"
              class="btn btn-sm btn-link p-0"
              @click="openRefundModal(transaction)"
              title="Classify refund"
            >
              <i class="bi bi-pencil"></i>
            </button>
            <button
              v-else-if="canEditTransaction(transaction) && props.isSystem !== 0"
              type="button"
              class="btn btn-sm btn-link p-0"
              @click="editTransaction(transaction)"
              title="Edit transaction"
            >
              <i class="bi bi-pencil"></i>
            </button>
          </div>
        </div>

        <!-- COGS Not Reversed row -->
        <div v-if="needsCOGSReversal" class="row border-bottom small align-items-center py-1">
          <div class="col-5 small fw-bold text-warning-emphasis">
            Cost of Goods Not Reversed
          </div>
          <div class="col-6 text-end d-flex gap-2 justify-content-end">
            <button
              type="button"
              class="btn btn-sm btn-outline-danger"
              :disabled="!!cogsProcessing"
              @click="handleReverseCogs"
            >
              <span v-if="cogsProcessing === 'reverse'" class="spinner-border spinner-border-sm"></span>
              <span v-else><i class="bi bi-arrow-counterclockwise me-1"></i>Reverse</span>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="!!cogsProcessing"
              @click="showCogsDismiss = !showCogsDismiss"
              title="Item not returned — keep cost of goods"
            >
              <i class="bi bi-eye-slash"></i>
            </button>
          </div>
          <div class="col-1">&nbsp;</div>
        </div>

        <!-- COGS dismiss inline form -->
        <div v-if="needsCOGSReversal && showCogsDismiss" class="row border-bottom small bg-light py-2 align-items-center">
          <div class="col-12 d-flex gap-2 align-items-center">
            <input
              type="text"
              v-model="cogsDismissReason"
              class="form-control form-control-sm"
              placeholder="Reason (optional) — e.g. lost in post, goodwill refund"
              @keyup.enter="handleDismissCogs"
              @keyup.escape="showCogsDismiss = false"
            />
            <button class="btn btn-sm btn-outline-secondary text-nowrap" :disabled="!!cogsProcessing" @click="handleDismissCogs">
              <span v-if="cogsProcessing === 'dismiss'" class="spinner-border spinner-border-sm"></span>
              <span v-else>Keep Cost of Goods</span>
            </button>
            <button class="btn btn-sm btn-link text-nowrap" @click="showCogsDismiss = false">Cancel</button>
          </div>
        </div>

        <!-- Shipping Not Assigned row — hidden for manual orders (use Edit Order instead) -->
        <div v-if="orderStatus === 'Completed' && props.isSystem !== 0" class="row border-bottom small align-items-center">

          <div class="col-5 small fw-bold text-danger-emphasis">
            <span v-if = "hasShippingLabel">
              Additional Shipping
            </span>
            <span v-else>
              Shipping Not Assigned
            </span>
          </div>
          <div class="col-6 text-end">
            <a href="#" @click.prevent="openShippingEditor"
                title="Additional Shipping"
            >
              &plus; shipping
            </a>
          </div>
          <div class="col-1">&nbsp;</div>
        </div>

        <!-- Edit Order row — manual orders only -->
        <div v-if="props.isSystem === 0" class="row border-bottom small align-items-center py-1">
          <div class="col-11 text-end">
            <button type="button" class="btn btn-sm btn-link p-0 text-muted" @click="emits('editOrder')">
              <i class="bi bi-pencil-square me-1"></i>Edit Order
            </button>
          </div>
          <div class="col-1">&nbsp;</div>
        </div>

      </div>
    </transition>

    <!-- Shipping Confirmation Modal -->
    <app-modal :modalIn="showShippingConfirm" @close="showShippingConfirm = false" modalClass="modal-sm">
      <template #header>Add Shipping</template>
      <div>
        <p class="mb-3">
          Shipping costs from eBay are imported automatically. Only add shipping here if it was
          handled outside of eBay (e.g., shipped separately, combined with another order, or
          through a different carrier).
        </p>
        <div class="d-flex gap-3 justify-content-between">
          <button class="btn btn-outline-secondary" @click="showShippingConfirm = false">Cancel</button>
          <button class="btn btn-primary" @click="confirmShippingEditor">Continue</button>
        </div>
      </div>
    </app-modal>

    <!-- Refund Classification Modal -->
    <refund-modal
      v-if="refundModalTransaction"
      :show="!!refundModalTransaction"
      :transaction="refundModalTransaction"
      :order-status="props.orderStatus"
      @close="refundModalTransaction = null"
      @updated="() => getOrderTransactions({ emitStatusChanged: true })"
    />

    <!-- Transaction Editor Modal -->
    <transaction-editor
      :show="showEditor"
      :order-id="props.orderId"
      :transaction-type="editorTransactionType"
      :existing-transaction="editingTransaction"
      @saved="onTransactionSaved"
      @close="closeEditor"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, computed, defineEmits} from "vue"
import { useStore } from "vuex"
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";
import TransactionEditor from "@/app/components/transactions/TransactionEditor.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
import RefundModal from "@/app/components/transactions/RefundModal.vue";
import { underscoreToWords } from "@/services/doliTools";

const store = useStore()
const transactions = ref([])
const loading = ref(false)
const props = defineProps(['orderId', 'orderStatus', 'platform', 'isSystem'])
const net = computed(() => transactions.value.reduce((acc, cur) => acc + parseFloat(cur.transaction_amount), 0.0))
const cogs = computed(() => transactions.value.filter(c => c.transaction_type.toLowerCase() === "cogs")[0]?.transaction_amount || 0)

const emits = defineEmits(['netUpdated', 'cogsUpdated', 'orderStatusChanged', 'editOrder'])

// Shipping detection
const hasShippingLabel = computed(() =>
  transactions.value.some(t => t.transaction_type === 'SHIPPING_LABEL')
)

// COGS reversal detection
// Note: getOrderTransactions already applies sign flip (DEBIT → negative, CREDIT → positive)
const hasCOGSDebit = computed(() =>
  transactions.value.some(t => t.transaction_type === 'COGS' && parseFloat(t.transaction_amount) < 0)
)
const hasCOGSCredit = computed(() =>
  transactions.value.some(t => t.transaction_type === 'COGS' && parseFloat(t.transaction_amount) >= 0)
)
const nonCOGSNet = computed(() =>
  transactions.value
    .filter(t => t.transaction_type !== 'COGS')
    .reduce((sum, t) => sum + parseFloat(t.transaction_amount || 0), 0)
)
const needsCOGSReversal = computed(() =>
  hasCOGSDebit.value && !hasCOGSCredit.value && Math.abs(nonCOGSNet.value) < 0.01
)

// COGS reversal/dismiss state
const cogsProcessing = ref(null)
const showCogsDismiss = ref(false)
const cogsDismissReason = ref('')

// const shippingTransaction = computed(() =>
//   transactions.value.find(t => t.transaction_type === 'SHIPPING_LABEL')
// )

// Refund modal state
const refundModalTransaction = ref(null)

function isRefund(transaction) {
  return transaction.transaction_type === 'REFUND'
}

function openRefundModal(transaction) {
  refundModalTransaction.value = transaction
}

// Editor state
const showEditor = ref(false)
const editorTransactionType = ref('SHIPPING_LABEL')
const editingTransaction = ref(null)
const showShippingConfirm = ref(false)

function transactionType(trans) {
  const label = trans.feeType || trans.transaction_type
  const base = underscoreToWords(label)
  if (trans.transaction_type === 'COGS') return 'Cost of Goods'
  if (trans.transaction_type === 'REFUND') {
    if (props.orderStatus === 'Returned') return `${base} (returned)`
    if (trans.ignored) return `${base} (${trans.transaction_memo || 'dismissed'})`
    return `${base} (unassigned)`
  }
  return base
}

// Check if transaction can be edited (is_system must be false/0, and not SALE type)
function canEditTransaction(transaction) {
  if (transaction.transaction_type === 'SALE') return false
  if (transaction.transaction_type === 'COGS') return false
  return transaction.is_system === 0 || transaction.is_system === false
}

function openShippingEditor() {
  if (props.platform === 'EBAY') {
    showShippingConfirm.value = true
  } else {
    confirmShippingEditor()
  }
}

function confirmShippingEditor() {
  showShippingConfirm.value = false
  editorTransactionType.value = 'SHIPPING_LABEL'
  editingTransaction.value = null
  showEditor.value = true
}

function editTransaction(transaction) {
  editorTransactionType.value = transaction.transaction_type
  editingTransaction.value = transaction
  showEditor.value = true
}

function closeEditor() {
  showEditor.value = false
  editingTransaction.value = null
}

function onTransactionSaved() {
  // Refresh transactions after save
  getOrderTransactions()
}

async function handleReverseCogs() {
  cogsProcessing.value = 'reverse'
  try {
    const result = await store.dispatch('eshop/reverseCogs', { orderId: props.orderId })
    if (result.success) {
      getOrderTransactions()
    } else if (result.noSourcePrice) {
      // No source_price — open manual COGS entry editor
      editorTransactionType.value = 'COGS'
      editingTransaction.value = null
      showEditor.value = true
    } else {
      alert(result.error || result.errors?.join('\n') || 'COGS reversal failed')
    }
  } catch (e) {
    alert(e.message || 'COGS reversal failed')
  }
  cogsProcessing.value = null
}

async function handleDismissCogs() {
  cogsProcessing.value = 'dismiss'
  try {
    await store.dispatch('eshop/dismissCogs', { orderId: props.orderId, reason: cogsDismissReason.value })
    showCogsDismiss.value = false
    cogsDismissReason.value = ''
    getOrderTransactions()
  } catch (e) {
    alert(e.message || 'Dismiss failed')
  }
  cogsProcessing.value = null
}

getOrderTransactions()

function getOrderTransactions({ emitStatusChanged = false } = {}) {
  store.dispatch("eshop/getOrderTransactions", { orderId: props.orderId })
      .then(data => {
        transactions.value = data
        emits('netUpdated', net.value)
        emits('cogsUpdated', cogs.value)
        if (emitStatusChanged) emits('orderStatusChanged')
        loading.value = false
      })
}
</script>

<style lang="scss" scoped>

</style>
